import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth-context";
import { LanguageToggle } from "./LanguageToggle";

/**
 * Global header — visible on every route except /login (which is a
 * dedicated full-screen form). Shows the brand on the left and a nav
 * cluster on the right. On mobile the nav collapses into a hamburger
 * menu; on desktop the links render inline. Items adapt to auth state:
 *   anon  → Превью / Встречи / Войти
 *   user  → Превью / Встречи / Профиль / Выйти
 *
 * The header pins to the top so navigation is always available while
 * scrolling long pages (events list, landing scenes).
 */
export function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { auth, clearUser } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const isRtl = i18n.dir() === "rtl";

  // Close the menu when the user navigates to a new route (e.g. by tapping
  // a link inside the menu) so it doesn't stay open over the next screen.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Esc closes the menu — a tiny ergonomic touch for desktop.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  const authed = auth.kind === "user";

  function handleLogout() {
    clearUser();
    setMenuOpen(false);
    navigate("/", { replace: true });
  }

  const links: { to: string; label: string; show: boolean }[] = [
    { to: "/", label: t("nav.preview"), show: true },
    { to: "/events", label: t("nav.events"), show: true },
    { to: "/profile", label: t("nav.profile"), show: authed },
  ];

  return (
    <header className="sticky top-0 z-40 bg-surface/90 backdrop-blur border-b border-ink-100">
      <div className="max-w-[1100px] mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
        <Link
          to="/"
          // Brand wordmark — no glyph icon, just the name set in our heaviest
          // weight with a touch of letter spacing so it reads as a logotype
          // rather than body text. Sized to occupy roughly a third of the
          // header on desktop without overpowering the nav cluster.
          className="shrink-0 font-black text-ink-900 tracking-[0.02em] text-[22px] sm:text-[26px] leading-none select-none hover:text-brand-purple transition-colors"
          style={{ fontFeatureSettings: '"ss01"' }}
        >
          {t("common.appName")}
        </Link>

        {/* Desktop nav — inline links + language switcher */}
        <nav className="hidden sm:flex items-center gap-1">
          {links.filter((l) => l.show).map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="px-3 py-1.5 text-[14px] font-semibold rounded-full text-ink-700 hover:bg-ink-50 transition-colors"
            >
              {l.label}
            </Link>
          ))}
          {authed ? (
            <button
              type="button"
              onClick={handleLogout}
              className="ml-2 px-3 py-1.5 text-[14px] font-semibold rounded-full text-ink-500 hover:text-ink-900 hover:bg-ink-50 transition-colors"
            >
              {t("nav.logout")}
            </button>
          ) : (
            <Link
              to="/login"
              className="ml-2 px-4 py-1.5 text-[14px] font-semibold rounded-full bg-brand-purple text-white shadow-sm hover:opacity-90 transition-opacity"
            >
              {t("nav.login")}
            </Link>
          )}
          <span className="ml-2"><LanguageToggle /></span>
        </nav>

        {/* Mobile — language toggle + burger button. */}
        <div className="sm:hidden flex items-center gap-1">
          <LanguageToggle />
          <button
            type="button"
            aria-label={menuOpen ? t("nav.closeMenu") : t("nav.openMenu")}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
            className="inline-flex flex-col justify-center items-center w-10 h-10 rounded-full hover:bg-ink-50 transition-colors"
          >
          {/* Animated burger ↔ X — three lines that morph */}
          <span
            className={`block w-5 h-[2px] bg-ink-900 rounded transition-transform duration-200 ${
              menuOpen ? "translate-y-[6px] rotate-45" : ""
            }`}
          />
          <span
            className={`block w-5 h-[2px] bg-ink-900 rounded my-1 transition-opacity duration-200 ${
              menuOpen ? "opacity-0" : ""
            }`}
          />
          <span
            className={`block w-5 h-[2px] bg-ink-900 rounded transition-transform duration-200 ${
              menuOpen ? "-translate-y-[6px] -rotate-45" : ""
            }`}
          />
          </button>
        </div>
      </div>

      {/* Mobile menu — a compact dropdown card anchored under the burger,
          floating over the page (doesn't push content down). Opens on the
          end side of the header: right for LTR (ru/en), left for RTL (he).
          A transparent backdrop below the bar closes it on an outside tap. */}
      {menuOpen && (
        <>
          <button
            type="button"
            aria-hidden
            tabIndex={-1}
            onClick={() => setMenuOpen(false)}
            className="sm:hidden fixed inset-x-0 bottom-0 top-14 z-40 cursor-default"
          />
          <nav
            className={`sm:hidden absolute top-full mt-1 z-50 min-w-[176px] rounded-2xl border border-ink-100 bg-surface shadow-[var(--shadow-card)] p-2 flex flex-col gap-1 ${
              isRtl ? "left-3" : "right-3"
            }`}
          >
            {links.filter((l) => l.show).map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setMenuOpen(false)}
                className="px-4 py-2.5 text-[15px] font-semibold rounded-xl text-ink-900 hover:bg-ink-50 transition-colors text-start"
              >
                {l.label}
              </Link>
            ))}
            {authed ? (
              <button
                type="button"
                onClick={handleLogout}
                className="px-4 py-2.5 text-[15px] font-semibold rounded-xl text-ink-500 text-start hover:bg-ink-50 transition-colors"
              >
                {t("nav.logout")}
              </button>
            ) : (
              <Link
                to="/login"
                onClick={() => setMenuOpen(false)}
                className="px-4 py-2.5 text-[15px] font-extrabold rounded-xl bg-brand-purple text-white text-center shadow-sm"
              >
                {t("nav.login")}
              </Link>
            )}
          </nav>
        </>
      )}
    </header>
  );
}
